import { useState } from 'react';
import { Section } from '../components/Section';
import { TOPICS_ROADMAP } from '../data/topics';

export function Roadmap(): JSX.Element {
  const initial = TOPICS_ROADMAP.find((t) => t.status === 'featured')?.ep ?? TOPICS_ROADMAP[0]?.ep ?? null;
  const [openEp, setOpenEp] = useState<string | null>(initial);
  const toggle = (ep: string) => setOpenEp((prev) => (prev === ep ? null : ep));

  return (
    <Section
      id="roadmap"
      label="THE ROADMAP"
      title={<>What's coming up.</>}
      kicker="A rolling slate of debates. Click any episode to see the candidate questions we plan to put on the table."
    >
      <div className="flex flex-col gap-3">
        {TOPICS_ROADMAP.map((t) => {
          const open = openEp === t.ep;
          const hasQs = t.questions.length > 0;
          const featured = t.status === 'featured';
          return (
            <div
              key={t.ep}
              className="overflow-hidden rounded-xl border-2 border-ink"
              style={{
                background: featured ? 'rgb(var(--color-duck))' : 'rgb(var(--color-paper))',
                opacity: t.status === 'past' ? 0.55 : 1,
              }}
            >
              <button
                type="button"
                onClick={() => hasQs && toggle(t.ep)}
                disabled={!hasQs}
                aria-expanded={open}
                className="grid w-full items-center gap-6 border-0 px-6 py-5 text-left disabled:cursor-default"
                style={{ gridTemplateColumns: '60px 110px 1fr 140px 36px', background: 'transparent' }}
              >
                <div className="font-display text-[28px] font-extrabold text-ink tabular-nums">EP{t.ep}</div>
                <div className="font-mono text-[13px] text-ink-soft">{t.date}</div>
                <div>
                  <div className="font-display text-lg font-bold text-ink">{t.title}</div>
                  <div className="mt-0.5 text-[13px] text-ink-soft">{t.blurb}</div>
                </div>
                <div className="text-right">
                  <span
                    className="rounded-full border-[1.5px] border-ink font-mono text-[11px] font-bold tracking-[0.08em]"
                    style={{
                      padding: '5px 10px',
                      background:
                        t.status === 'past'
                          ? 'rgb(var(--color-ink) / 12%)'
                          : featured
                            ? 'rgb(var(--color-ink))'
                            : 'rgb(var(--color-bg))',
                      color: featured ? 'rgb(var(--color-bg))' : 'rgb(var(--color-ink))',
                    }}
                  >
                    {t.status.toUpperCase()}
                  </span>
                </div>
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-ink font-display text-lg font-extrabold leading-none"
                  style={{
                    background: open ? 'rgb(var(--color-ink))' : 'transparent',
                    color: open ? 'rgb(var(--color-bg))' : 'rgb(var(--color-ink))',
                    transform: open ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 200ms ease',
                    visibility: hasQs ? 'visible' : 'hidden',
                  }}
                >
                  ⌄
                </div>
              </button>
              {open && hasQs && (
                <div
                  className="border-t-2 border-dashed border-ink px-6 pt-5 pb-[22px]"
                  style={{ background: featured ? 'rgba(255,255,255,0.55)' : 'rgb(var(--color-bg))' }}
                >
                  <div className="mb-3.5 font-mono text-[11px] uppercase tracking-[0.16em] text-ink-soft">
                    Candidate questions · {t.questions.length} on the table
                  </div>
                  <ol className="m-0 grid list-none gap-y-2.5 gap-x-7 p-0" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                    {t.questions.map((q, i) => (
                      <li key={i} className="grid items-baseline gap-2.5 text-sm leading-snug text-ink" style={{ gridTemplateColumns: '32px 1fr' }}>
                        <span className="pt-0.5 font-mono text-xs font-bold text-beak tabular-nums">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4 border-t border-[rgb(var(--color-ink)/12%)] pt-3.5 font-mono text-[11px] italic text-ink-soft">
                    Working list — order and phrasing get sharpened as we get closer to taping.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}
